import { useState, useEffect, useMemo, useCallback } from "react";
import PageTemplate from "../../../Templates/PageTemplate";
import middleware from "../../../Api/Middleware";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router";
import "../../../Main/Main.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
import TablePreset from "../../../Table/TablePreset";
import CheckboxHeader from "../../../Table/TableComponents/CheckboxHeader";
import SortableHeader from "../../../Table/TableComponents/SortableHeader";
import { useTable } from "react-table";
import CheckboxCell from "../../../Table/TableComponents/CheckboxCell";
import {
  BorderedNumberInput,
  BorderedTextInput,
} from "../../../HouseStyle/Components/Inputs";
import { BackButton, ButtonBlue, ButtonLightGrey } from "../../../HouseStyle/Components/Buttons";
import ToastError from "../../../Toasts/ToastError";
import Filters from "../../../Filters/Filters";
import ToastSuccess from "../../../Toasts/ToastSuccess";
import ToastPromise from "../../../Toasts/ToastPromise";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function SizeSettings() {
  const cookies = new Cookies();
  const navigate = useNavigate();
  
  // Filter logic
  const defaultFilters = [
    {
      index: 0,
      order: 1,
      value: 1,
      name: "Status maat",
      field: "status",
      type: "radio",
      options: [
        { name: "Gepubliceerd", value: 1 },
        { name: "Gearchiveerd", value: 0 },
      ],
    },
  ];

  const [tempfilters, setTempFilters] = useState(defaultFilters);
  const [filters, setFilters] = useState(defaultFilters);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page_size: 10,
    page: 1,
    max_items: 0,
    begin_of_page: 0,
    end_of_page: 0,
  }); // the useState for the pagination for the table
  const [order, setOrder] = useState({ orderBy: "", direction: "" }); // useState for ordering the columns in the table
  const [selectedIds, setSelectedIds] = useState([]); // usestate for the selected checkboxes
  const [searchBar, setSearchBar] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [show, setShow] = useState(false);
  const [sizeNameNew, setSizeNameNew] = useState("");
  const [sizeName, setSizeName] = useState("");
  const [dataEditSizeModal, setDataEditSizeModal] = useState([]);
  const [showNewSizeModal, setShowNewSizeModal] = useState(false);
  const [showEditSizeModal, setShowEditSizeModal] = useState(false);
  const handleCloseNewSize = () => setShowNewSizeModal(false);
  const handleCloseEditSize = () => setShowEditSizeModal(false);

  // Filter modal close function
  const CloseFilterModal = () => {
    setShowFilterModal(false);
  };

  const debouncedSearchInput = useDebounce(searchInput, 300);
   useEffect(() => {
    setSearchBar(debouncedSearchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearchInput]);

  // Modified handleSearchChange to only update input state
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const FetchSizes = async () => {
    setLoading(true);
    try {
      const searchQuery = searchBar.trim()
        ? `&searchTerm=${encodeURIComponent(
          searchBar.trim()
        )}&noSearch=[size_id,status,sorting_order,updated_at,created_at,updater_user_id,creator_user_id]`
        : "";
      const orderQuery =
        order.orderBy && order.direction
          ? `&orderBy=${order.orderBy}&orderDirection=${order.direction}`
          : "";

      // Add filter logic to API call
      const response = await middleware.get(
        `products/sizes?page=${pagination.page}&page_size=${pagination.page_size}` +
        orderQuery +
        searchQuery
      );

      let data = response.data.data.data;
      
      // Apply client-side filtering based on active filters
      const activeFilters = filters.filter((e) => e.value != null && e.value !== "");
      if (activeFilters.length > 0) {
        data = data.filter(size => {
          return activeFilters.every(filter => {
            return String(size[filter.field]) === String(filter.value);
          });
        });
      }

      setSizes(data);
      setPagination(prev => ({
        ...prev,
        page: pagination.page > response.data.data.last_page ? response.data.data.last_page : pagination.page,
        max_items: response.data.data.total,
        begin_of_page: response.data.data.from,
        end_of_page: response.data.data.to,
      }));
    } catch (error) {
      if (error.response?.status === 401) {
        cookies.remove("token", { path: "/" });
        navigate("/");
      } else {
        console.error("Error fetching sizes:", error);
      }
    } finally {
      // Altijd loader resetten, ongeacht succes of failure
      setLoading(false);
    }
  };

  useEffect(() => {
    FetchSizes();
  }, [
    pagination.page,
    pagination.page_size,
    order.orderBy,
    order.direction,
    searchBar,
    filters, // Added filters to dependency array
  ]);

  // Handle checkbox selection
  const handleSelection = (productId, isSelected) => {
    if (isSelected) {
      setSelectedIds([...selectedIds, productId]);
    } else {
      setSelectedIds(selectedIds.filter((id) => id !== productId));
    }
  };

  function handleShow() {
    if (selectedIds.length > 0) {
      setShow(true);
    } else {
      ToastError(
        "Geen maten geselecteerd, selecteer een maat om te deactiveren"
      );
    }
  }

  const deactivateSize = async () => {
    try {
      const promises = selectedIds.map((id) =>
        middleware.put(`products/sizes`, {
          size_id: id,
          status: 0,
        })
      );
      await Promise.all(promises);
      setSelectedIds([]);
      FetchSizes();
    } catch (error) {
      console.error("Error deactivating sizes:", error);
    }
  };

  const ActivateSizes = async () => {
    try {
      const promises = selectedIds.map(id =>
        middleware.put(`products/sizes`, {
          size_id: id,
          status: 1,
        }));
      await Promise.all(promises);
      setSelectedIds([]);
      FetchSizes();
    } catch (error) {
      console.error("Error activating sizes:", error);
    }
  };

  const submitEditSizes = async (event) => {
    event.preventDefault();

    try {
      // Als er geen wijzigingen zijn, sluit gewoon de modal
      if (!sizeName || sizeName === dataEditSizeModal.main) {
        setShowEditSizeModal(false);
        return;
      }

      await middleware.put(`products/sizes`, {
        size_id: dataEditSizeModal.size_id,
        main: sizeName,
      });

      setShowEditSizeModal(false);
      FetchSizes();
      
      ToastSuccess(`Maat succesvol gewijzigd naar "${sizeName}"`, 'size_edited');
      
    } catch (error) {
      console.error(error);
    
      // Check if it's a duplicate entry error
      if (error.response?.data?.error) {
        const errorMessage = error.response.data.error;
        
        // Check for duplicate entry error (1062 is MySQL duplicate entry error code)
        if (errorMessage.includes('Duplicate entry') || errorMessage.includes('1062')) {
          ToastError(`Maat "${sizeName}" bestaat al. Kies een andere naam.`, 'duplicate_size');
        } else {
          // Generic error message for other database errors
          ToastError("Er is een fout opgetreden bij het wijzigen van de maat.", 'size_edit_error');
        }
      } else {
        // Fallback error message
        ToastError("Er is een onbekende fout opgetreden. Probeer het opnieuw.", 'unknown_error');
      }
    }
  };

  const submitSizes = async (event) => {
    event.preventDefault();

    if (sizeNameNew == "" || sizeNameNew == null) {
      ToastError("Voer een maat in.", 'fill_in_size');
      return;
    }

    try {
      await middleware.post(`products/sizes`, {
        main: sizeNameNew,
      });

      setSizeNameNew("");
      setShowNewSizeModal(false);
      FetchSizes();
    } catch (error) {
      // Check if it's a duplicate entry error
      if (error.response?.data?.error) {
        const errorMessage = error.response.data.error;
        
        // Check for duplicate entry error (1062 is MySQL duplicate entry error code)
        if (errorMessage.includes('Duplicate entry') || errorMessage.includes('1062')) {
          ToastError(`Maat "${sizeNameNew}" bestaat al. Kies een andere naam.`, 'duplicate_size');
        } else {
          // Generic error message for other database errors
          ToastError("Er is een fout opgetreden bij het toevoegen van de maat.", 'size_add_error');
        }
      } else {
        // Fallback error message
        ToastError("Er is een onbekende fout opgetreden. Probeer het opnieuw.", 'unknown_error');
      }
    }
  };

  const openSizeModal = (modal) => {
    if (modal == "maat") {
      setShowNewSizeModal(true);
    }
  };

  const DeleteSizes = async () => {
    await ToastPromise({
      promise: (async () => {
        // Execute delete request
        const response = await middleware.delete(`products/sizes?size_ids=${selectedIds.join(',')}`);
        
        // Check if any sizes were actually deleted
        if (response.data?.summary) {
          const { total, deleted, errors } = response.data.summary;
          
          // Handle different scenarios
          if (deleted === 0 && errors > 0) {
            // Nothing deleted, all errors
            throw new Error('no_deletions');
          }
          
          if (deleted > 0) {
            // At least some were deleted, refresh the list
            FetchSizes();
            
            // Update selection to only keep failed IDs
            if (errors > 0) {
              const failedIds = response.data.results
                .filter(r => r.status === 'error')
                .map(r => r.size_id);
            }
          }
        }
        
        setSelectedIds([]);

        return response;
      })(),
      pending: 'Maten worden verwijderd...',
      successRenderer: (response) => {
        const data = response?.data;
        
        if (!data?.summary) {
          return 'Maten verwijderd';
        }
        
        const { total, deleted, errors } = data.summary;
        
        // Build success message based on results
        if (deleted === total) {
          // All successfully deleted
          return deleted === 1 
            ? 'Maat succesvol verwijderd' 
            : `Alle ${deleted} maten succesvol verwijderd`;
        }
        
        if (deleted > 0 && errors > 0) {
          // Partial success
          const inUseCount = data.results.filter(r => 
            r.error?.includes('in use')
          ).length;
          
          if (inUseCount === errors) {
            return `${deleted} van ${total} maten verwijderd. ${errors} ${errors === 1 ? 'maat is' : 'maten zijn'} nog in gebruik.`;
          }
          
          return `${deleted} van ${total} maten verwijderd. ${errors} ${errors === 1 ? 'fout' : 'fouten'} opgetreden.`;
        }
        
        // Should not reach here if throw worked correctly
        return 'Actie voltooid';
      },
      errorRenderer: (error) => {
        console.error("Error deleting sizes:", error);
        
        // Check if it's our custom error for no deletions
        if (error?.message === 'no_deletions') {
          const response = error.response || {};
          const data = response.data || {};
          
          if (data.summary?.errors > 0) {
            const inUseCount = data.results?.filter(r => 
              r.error?.includes('in use')
            ).length || 0;
            
              if (inUseCount === data.summary.errors) {
                return data.summary.errors === 1
                  ? 'Deze maat kan niet worden verwijderd omdat deze nog aan een of meer producten is gekoppeld.'
                  : `Deze ${data.summary.errors} maten kunnen niet worden verwijderd omdat ze nog aan een of meer producten zijn gekoppeld.`;
              }
          }
          // Altijd een duidelijke melding geven als fallback
          return 'De geselecteerde maten kunnen niet worden verwijderd omdat ze nog aan een of meer producten zijn gekoppeld.';
        }
        
        // Handle HTTP error codes
        if (error?.response?.status === 403) {
          return 'Je hebt geen toestemming om deze maten te verwijderen';
        }
        
        if (error?.response?.status === 404) {
          return 'Een of meer maten niet gevonden';
        }
        
        return 'Fout bij het verwijderen van maten. Probeer het opnieuw.';
      },
      autoClose: 4000
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <CheckboxHeader
            currentItem={sizes}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            pk={"size_id"}
          ></CheckboxHeader>
        ),
        accessor: "select",
        Cell: ({ row }) => {
          return (
            <CheckboxCell
              row={row}
              handleSelection={handleSelection}
              selectedIds={selectedIds}
              pk={"size_id"}
            />
          );
        },
      },
      {
        Header: () => (
          <SortableHeader
            currentItem={order}
            setItem={setOrder}
            field="main"
            pos={"start"}
          >
            Maat
          </SortableHeader>
        ),
        accessor: "main",
        Cell: ({ row }) => {
          return (
            <a
              className="FlatTextLink text-decoration-none text-nowrap"
              onClick={() => {
                setShowEditSizeModal(true);
                setSizeName(row.original.main);
                setDataEditSizeModal(row.original);
              }}
            >
              {row.original.main}
            </a>
          );
        },
      },
            {
        Header: () => (
          <SortableHeader
            currentItem={order}
            setItem={setOrder}
            field={"status"}
            pos={"end"}
          >
            Status
          </SortableHeader>
        ),
        accessor: "status",
        Cell: ({ row }) => {
          return <div className="text-end" style={{ color: row.original.status == "1" ? "#A9D974" : "#ffcc9d" }}>
            {row.original.status == "1" ? "Gepubliceerd" : "Gearchiveerd"}
          </div>
        }
      },
    ],
    [order.orderBy, order.direction, selectedIds, setSelectedIds, sizes]
  );

  //   Table instance
  const tableInstance = useTable({ columns, data: sizes });

  // Destructure table properties
  // const handleSearchChange = useCallback((value) => {
  //   setSearchBar(value);
  //   setPagination((prev) => ({ ...prev, page: 1 }));
  // }, []);

  return (
    <>
      <Modal
        id="modalBlueprint"
        show={showEditSizeModal}
        onHide={handleCloseEditSize}
        centered
        size="xl"
      >
        <Modal.Header className="modalTop d-flex justify-content-between">
          <Modal.Title className="ms-3 BlockTitle ">Edit maat</Modal.Title>
          <button
            className="border-0 bg-transparent"
            variant="transparent"
            onClick={handleCloseEditSize}
          >
            <FontAwesomeIcon icon={faXmark} className="closeModal" />
          </button>
        </Modal.Header>
        <form>
          <Modal.Body className="modalCenter pb-0 justify-content-around">
            <div className="w-100">
              <div>
                <label className="FlatText">Maat naam*</label>
                <BorderedTextInput
                  className="optionsList optionListWidt text-center mb-3 w-100"
                  placeholder="Maat naam"
                  name="size_name"
                  inputValue={sizeName}
                  setInputValue={setSizeName}
                ></BorderedTextInput>
                <small className="BeneathFlatText">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="text-warning me-1"
                  ></FontAwesomeIcon>
                  Let op bij het opslaan van deze maat naam zou het doorgevoerd
                  worden naar waar deze naam aan gekoppelt is
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="text-warning ms-1"
                  ></FontAwesomeIcon>
                </small>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="modalBottom">
            <ButtonBlue
              text={"Opslaan"}
              onClickFunction={(e) => submitEditSizes(e)}
            />
          </Modal.Footer>
        </form>
      </Modal>

      {/* de-activeren Modal */}
      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        size="xl"
      >
        <Modal.Header className="modalTop d-flex justify-content-between">
          <Modal.Title className="ms-3 BlockTitle ">Let op!</Modal.Title>
          <button
            className="border-0 bg-transparent"
            variant="transparent"
            onClick={() => setShow(false)}
          >
            <FontAwesomeIcon icon={faXmark} className="closeModal" />
          </button>
        </Modal.Header>
        <Modal.Body className="modalCenter pb-0 justify-content-around">
          <div className="ms-3">
            U gaat {selectedIds.length}{" "}
            {selectedIds.length === 1 ? "item" : "items"} archiveren. Weet U
            dat zeker?
          </div>
        </Modal.Body>
        <Modal.Footer className="modalBottom">
          <div className="d-flex flex-row">
            <div className="me-3">
              <ButtonLightGrey
                text={"Annuleren"}
                className="ms-3 btn bg-danger text-white"
                onClickFunction={() => setShow(false)}
              />
            </div>
            <ButtonBlue
              text={"Archiveren"}
              onClickFunction={() => {
                setShow(false);
                deactivateSize();
              }}
            />
          </div>
        </Modal.Footer>
      </Modal>

      {/* Maat toevoegen Modal */}
      <Modal
        id="modalBlueprint"
        show={showNewSizeModal}
        onHide={handleCloseNewSize}
        centered
        size="xl"
      >
        <Modal.Header className="modalTop d-flex justify-content-between">
          <h4 className="mb-0 fw-semifold">Nieuwe maat</h4>
          <button
            className="border-0 bg-transparent"
            variant="transparent"
            onClick={handleCloseNewSize}
          >
            <FontAwesomeIcon icon={faXmark} className="closeModal" />
          </button>
        </Modal.Header>
        <form onSubmit={submitSizes}>
          <Modal.Body className="modalCenter pb-0 justify-content-around">
            <div className="w-100">
              <div className="mb-3">
                <label className="FlatText">Maat*</label>
                <BorderedTextInput
                  placeholder="Maat"
                  required={true}
                  inputValue={sizeNameNew}
                  setInputValue={setSizeNameNew}
                ></BorderedTextInput>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="modalBottom">
            <div className="">
            <ButtonBlue
              text={"Aanmaken"}
              onClickFunction={(e) => submitSizes(e)}
              type="submit"
            />
            </div>
          </Modal.Footer>
        </form>
      </Modal>
      <PageTemplate
        navbarTitle={"Maten Overzicht"}
      >
        <div className="w-100">
          <div>
            <BackButton
              text={'Terug'}
              onClickFunction={() => navigate('/algemene/instellingen/product')}
            />
          </div>

          <TablePreset
            data={[
              {
                selectedIds: selectedIds,
                tableInstance: tableInstance,
                pagination: pagination,
                primary_key: "size_id",
              },
            ]}
            tableLoading={loading}
            setPagination={setPagination}
            handleSelection={handleSelection}
            searchBar={[{
              placeholder: 'Zoek op maat...',
              shown: true,
              searchFunction: handleSearchChange,
              value: searchInput
            }]}
            actionOptions={[
              { title: "Archiveren", function: () => handleShow() },
              { title: "Publiceren", function: () => ActivateSizes() },
              { title: "Verwijderen", function: () => DeleteSizes() }
            ]}
            leftButtons={[
              { title: "Filteren", function: () => setShowFilterModal(true) }
            ]}
            rightButtons={[
              { title: "Nieuwe maat", function: () => openSizeModal("maat") },
            ]}
          />
        </div>
        <Filters
          filters={tempfilters}
          showFilterModal={showFilterModal}
          closeFilterModal={CloseFilterModal}
          setTempFilters={setTempFilters}
          setFilters={setFilters}
          defaultFilters={defaultFilters}
        />
      </PageTemplate>
    </>
  );
}

export default SizeSettings;
